import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Table, Button, FloatingLabel, Form } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import { get } from '../../services/apiController';
import saveAs from 'file-saver';

const MonthWiseVendorStockReport = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [state, setState] = useState({
    factoryId: 'all',
    categoryId: '',
    year: '2025-26'
  });

  useEffect(() => {
    fetchCategories();
    fetchBranches();
  }, []);

  // Filter data based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(item =>
        item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredData(filtered);
    }
  }, [data, searchTerm]);

  const fetchCategories = () => {
    get('/inv/item-categories')
      .then((res) => {
        setCategories(res?.data || []);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
      });
  };

  const fetchBranches = () => {
    get('/branches?type=factory')
      .then((res) => {
        setBranches(res?.data?.rows || []);
      })
      .catch((err) => {
        console.error('Error fetching branches:', err);
      });
  };

  const getMasterlist = (downloadExcel = false) => {
    if (downloadExcel) {
      setDownloadingExcel(true);
    } else {
      setLoading(true);
    }

    let url = `/inv/report/monthly-vendor-price-change-report/?`;

    const params = new URLSearchParams();
    if (state.factoryId) params.append('factoryId', state.factoryId);
    if (state.categoryId) params.append('categoryId', state.categoryId);
    if (state.year) params.append('year', state.year);
    if (downloadExcel) params.append('downloadExcel', 'true');

    url += params.toString();

    get(url, { responseType: downloadExcel ? 'arraybuffer' : 'json' })
      .then((res) => {
        if (downloadExcel) {
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const fileName = `Monthly_Vendor_Price_Change_Report_${state.year}_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`;
          saveAs(blob, fileName);
          toast.success('Excel file downloaded successfully!');
        } else {
          const newData = res?.data?.data || [];
          setData(newData);
          setFilteredData(newData);
          setTotalRecords(newData.length);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || err?.message);
      })
      .finally(() => {
        if (downloadExcel) {
          setDownloadingExcel(false);
        } else {
          setLoading(false);
        }
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);
  };

  // Group items by category for hierarchical rendering
  const groupedByCategory = React.useMemo(() => {
    const groups = {};
    (filteredData || []).forEach((item) => {
      if (!groups[item.categoryName]) groups[item.categoryName] = [];
      groups[item.categoryName].push(item);
    });
    return groups;
  }, [filteredData]);

  return (
    <>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <div className="col-3">
            <div className="d-flex" style={{ height: '100%', alignItems: 'center' }}>
              <h4 className="card-title" style={{ fontSize: '16px' }}>
                Monthly Vendor Price Change Report | {filteredData.length ?? 0}
                {searchTerm && filteredData.length !== totalRecords && (
                  <span className="text-muted small"> (filtered from {totalRecords})</span>
                )}
              </h4>
            </div>
          </div>
          <div className="card-header-actions col-9 d-flex justify-content-end">
            <div style={{ maxWidth: '180px', marginRight: '10px' }}>
              <FloatingLabel controlId="itemSearch" label="Search Items">
                <Form.Control
                  type="text"
                  placeholder="Search items, codes, vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </FloatingLabel>
            </div>
            <div style={{ maxWidth: '130px', marginRight: '10px' }}>
              <FloatingLabel controlId="yearSelect" label="Year">
                <Form.Select
                  value={state.year}
                  onChange={(e) => {
                    setState({ ...state, year: e.target.value });
                  }}
                >
                  <option value="2025-26">2025-26</option>
                  <option value="2024-25">2024-25</option>
                  <option value="2023-24">2023-24</option>
                  <option value="2022-23">2022-23</option>
                </Form.Select>
              </FloatingLabel>
            </div>

            <div style={{ maxWidth: '130px', marginRight: '10px' }}>
              <FloatingLabel controlId="factorySelect" label="Factory">
                <Form.Select
                  value={state.factoryId}
                  onChange={(e) => {
                    setState({ ...state, factoryId: e.target.value });
                  }}
                >
                  <option value="all">All Factories</option>
                  {branches?.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </div>

            <div style={{ maxWidth: '130px', marginRight: '20px' }}>
              <FloatingLabel controlId="categorySelect" label="Category">
                <Form.Select
                  value={state.categoryId}
                  onChange={(e) => {
                    setState({ ...state, categoryId: e.target.value });
                  }}
                >
                  <option value="">All Categories</option>
                  {categories?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </div>
            <div className="d-flex" style={{ maxWidth: '250px', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
              <Button
                onClick={() => getMasterlist(false)}
                type="submit"
                className="btn app-btn-default dark-color"
                disabled={loading || downloadingExcel}
              >
                {loading ? 'Loading...' : 'Apply'}
              </Button>

              <Button
                onClick={() => getMasterlist(true)}
                className="btn btn-success"
                disabled={loading || downloadingExcel}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                {downloadingExcel ? 'Downloading...' : 'Download Excel'}
              </Button>
            </div>
          </div>
        </div>

        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              {loading && (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {!loading && (
                <Table responsive striped bordered style={{ border: '1px solid #06020775' }}>
                  <thead>
                    <tr>
                      <th rowSpan={2} className="text-center align-middle">S.NO</th>
                      <th rowSpan={2} className="text-center align-middle">MAIN INGREDIENT</th>
                      <th rowSpan={2} className="text-center align-middle">UOM</th>
                      <th rowSpan={2} className="text-center align-middle">FACTORY</th>
                      <th rowSpan={2} className="text-center align-middle sepration-border">SUPPLIER NAME</th>
                      <th colSpan={12} className="text-center rate-section sepration-border">RATE (Avg Month Wise)</th>
                      <th colSpan={12} className="text-center purchased-stock-section sepration-border">Total Purchased Stock</th>
                      <th colSpan={12} className="text-center utilized-stock-section sepration-border">Total Utilized Stock</th>
                    </tr>
                    <tr>
                      {/* Rate month headers */}
                      {filteredData.length > 0 && filteredData[0].fiscalMonths?.map((month) => (
                        <th key={`rate-${month.alias}`} className={`text-center ${month.alias === 'mar' ? 'sepration-border' : ''} rate-section`}>{month.label}</th>
                      ))}
                      {/* Purchased stock month headers */}
                      {filteredData.length > 0 && filteredData[0].fiscalMonths?.map((month) => (
                        <th key={`purchased-${month.alias}`} className={`text-center ${month.alias === 'mar' ? 'sepration-border' : ''} purchased-stock-section`}>{month.label}</th>
                      ))}
                      {/* Utilized stock month headers */}
                      {filteredData.length > 0 && filteredData[0].fiscalMonths?.map((month) => (
                        <th key={`utilized-${month.alias}`} className={`text-center ${month.alias === 'mar' ? 'sepration-border' : ''} utilized-stock-section`}>{month.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedByCategory).map(([categoryName, items]) => (
                      <React.Fragment key={categoryName}>
                        {/* Category Row */}
                        <tr className="table-secondary" style={{ border: '1px solid #06020775' }}>
                          <td colSpan={41}>
                            <strong>{categoryName}</strong> ({items.length} items)
                          </td>
                        </tr>

                        {items.map((item, index) => {
                          const vendors = item.vendorMonthlyPrices || [];
                          const rowSpan = Math.max(1, vendors.length);
                          const firstVendor = vendors[0];
                          return (
                            <React.Fragment key={item.itemId}>
                              {/* First combined row */}
                              <tr>
                                <td className="text-center" rowSpan={rowSpan}>{index + 1}</td>
                                <td rowSpan={rowSpan}>
                                  <div>
                                    <strong>{item.itemName}</strong>
                                    {item.itemCode && (
                                      <div className="text-muted small">Code: {item.itemCode}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center" rowSpan={rowSpan}>{item.unit}</td>
                                <td className="text-center" rowSpan={rowSpan}>{item.factoryName || '-'}</td>
                                <td className="text-center sepration-border">
                                  {firstVendor ? <strong>{firstVendor.vendorName}</strong> : <span className="text-muted">-</span>}
                                </td>
                                {item.fiscalMonths?.map((month) => (
                                  <td key={`price-first-${month.alias}-${item.itemId}`} className={`text-end ${month.alias === 'mar' ? 'sepration-border' : ''} rate-section`}>
                                    {firstVendor && firstVendor.monthlyPrices?.[month.alias] > 0
                                      ? formatCurrency(firstVendor.monthlyPrices[month.alias])
                                      : '-'}
                                  </td>
                                ))}
                                {item.fiscalMonths?.map((month) => (
                                  <td key={`purchased-${month.alias}-${item.itemId}`} className={`text-end ${month.alias === 'mar' ? 'sepration-border' : ''} purchased-stock-section`}>
                                    {firstVendor && firstVendor.monthlyPurchasedStock?.[month.alias] > 0 
                                      ? formatNumber(firstVendor.monthlyPurchasedStock[month.alias]) 
                                      : '-'}
                                  </td>
                                ))}
                                {item.fiscalMonths?.map((month) => (
                                  <td key={`utilized-${month.alias}-${item.itemId}`} className={`text-end ${month.alias === 'mar' ? 'sepration-border' : ''} utilized-stock-section`} rowSpan={rowSpan}>
                                    {item.monthlyUtilizedStock?.[month.alias] > 0 ? formatNumber(item.monthlyUtilizedStock[month.alias]) : '-'}
                                  </td>
                                ))}
                              </tr>

                              {/* Remaining vendors */}
                              {vendors.slice(1).map((vendor) => (
                                <tr key={`vendor-${item.itemId}-${vendor.vendorId}`}>
                                  <td className="text-center sepration-border">
                                    <strong>{vendor.vendorName}</strong>
                                  </td>
                                  {item.fiscalMonths?.map((month) => (
                                    <td key={`vprice-${month.alias}-${item.itemId}-${vendor.vendorId}`} className={`text-end ${month.alias === 'mar' ? 'sepration-border' : ''} rate-section`}>
                                      {vendor.monthlyPrices?.[month.alias] > 0
                                        ? formatCurrency(vendor.monthlyPrices[month.alias])
                                        : '-'}
                                    </td>
                                  ))}
                                  {item.fiscalMonths?.map((month) => (
                                    <td key={`vpurchased-${month.alias}-${item.itemId}-${vendor.vendorId}`} className={`text-end ${month.alias === 'mar' ? 'sepration-border' : ''} purchased-stock-section`}>
                                      {vendor.monthlyPurchasedStock?.[month.alias] > 0
                                        ? formatNumber(vendor.monthlyPurchasedStock[month.alias])
                                        : '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonthWiseVendorStockReport;